'use client';
import React from 'react';
import AppImage from '@/components/ui/AppImage';
import MomentumBadge from '@/components/ui/MomentumBadge';
import FitScoreBadge from '@/components/ui/FitScoreBadge';
import Icon from '@/components/ui/AppIcon';
import type { Creator } from './FindCreatorClient';

interface Props {
  creators: Creator[];
  onClose: () => void;
  onRemove: (id: string) => void;
}

const compareRows = [
  { id: 'cmp-followers', label: 'Followers', key: 'followersDisplay' },
  { id: 'cmp-engagement', label: 'Engagement', key: 'engagementRate', suffix: '%' },
  { id: 'cmp-audience-dz', label: 'Audience DZ', key: 'audienceAlgeria', suffix: '%' },
  { id: 'cmp-audience-q', label: 'Audience Quality', key: 'audienceQuality', suffix: '/100' },
  { id: 'cmp-momentum', label: 'Momentum Score', key: 'momentumScore' },
  { id: 'cmp-fit', label: 'Brand Fit Score', key: 'fitScore', suffix: '%' },
];

export default function ComparisonDrawer({ creators, onClose, onRemove }: Props) {
  return (
    <div className="fixed bottom-0 left-0 right-0 z-40 animate-slide-up">
      <div className="glassmorphism border-t border-border shadow-2xl">
        <div className="max-w-screen-2xl mx-auto px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-700 text-foreground">Comparaison des créateurs</h3>
            <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground">
              <Icon name="XMarkIcon" size={16} />
            </button>
          </div>

          <div className="grid gap-4" style={{ gridTemplateColumns: `160px repeat(${creators.length}, 1fr)` }}>
            {/* Labels column */}
            <div className="space-y-3">
              <div className="h-16" />
              {compareRows.map((row) => (
                <div key={row.id} className="h-8 flex items-center">
                  <span className="text-xs text-muted-foreground">{row.label}</span>
                </div>
              ))}
            </div>

            {/* Creator columns */}
            {creators.map((creator) => (
              <div key={`cmp-col-${creator.id}`} className="space-y-3">
                <div className="flex items-center gap-2 h-16">
                  <div className="w-10 h-10 rounded-full overflow-hidden border border-border flex-shrink-0">
                    <AppImage src={creator.avatar} alt={creator.avatarAlt} width={40} height={40} className="w-full h-full object-cover" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs font-700 text-foreground truncate">{creator.name}</p>
                    <MomentumBadge score={creator.momentumScore} size="sm" />
                  </div>
                  <button onClick={() => onRemove(creator.id)} className="p-1 rounded text-muted-foreground hover:text-foreground ml-auto flex-shrink-0">
                    <Icon name="XMarkIcon" size={12} />
                  </button>
                </div>

                {compareRows.map((row) => {
                  const val = creator[row.key as keyof Creator];
                  return (
                    <div key={`cmp-${creator.id}-${row.id}`} className="h-8 flex items-center">
                      {row.key === 'fitScore' ? (
                        <FitScoreBadge score={creator.fitScore} size="sm" />
                      ) : (
                        <span className="text-sm font-700 text-foreground font-mono">
                          {String(val)}{row.suffix ?? ''}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}